'use client'
import type { Category, Discount, Product, Variant } from '@/payload-types'
import { calculateDiscountedPrice } from '@/lib/productDiscounts'

import { RichText } from '@/components/RichText'
import { AddToCart } from '@/components/Cart/AddToCart'
import { ShareButton } from '@/components/ShareButton'
import { Price } from '@/components/Price'
import React, { Suspense } from 'react'
import { useSearchParams } from 'next/navigation'

import { VariantSelector } from './VariantSelector'
import { useCurrency } from '@payloadcms/plugin-ecommerce/client/react'
import { StockIndicator } from '@/components/product/StockIndicator'
import { BulkDiscountTable } from './BulkDiscountTable'
import { Badge } from '@/components/ui/badge'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { trackViewItem } from '@/utilities/gtag'

import { useAuth } from '@/providers/Auth'
import { useLocale } from 'next-intl'
import { WishlistButton } from '@/components/WishlistButton'

export function ProductDescription({
  product,
  bulkDiscounts,
  manualDiscounts,
}: {
  product: Product
  bulkDiscounts?: Discount[]
  manualDiscounts?: Discount[]
}) {
  const t = useTranslations('product')
  const { currency } = useCurrency()
  const searchParams = useSearchParams()
  const { user } = useAuth()
  const locale = useLocale()

  const [hoverPrice, setHoverPrice] = React.useState<number | null>(null)

  // GA4: fire view_item once on mount
  React.useEffect(() => {
    const basePrice = product.basePrice ?? 0
    trackViewItem(product, basePrice, 'EUR')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id])
  let amount = 0,
    lowestAmount = 0,
    highestAmount = 0

  const variants = product.variants || []
  const hasVariants = product.enableVariants && Array.isArray(variants) && variants.length > 0

  const categories = product.categories?.filter((c): c is Category => typeof c === 'object') || []

  // Calculate price based on selection or range
  const selectedPrices: number[] = []
  let selectedVariantPrice: number | null = null

  if (hasVariants) {
    variants.forEach((variant) => {
      const selectedValue = searchParams.get(variant.name.toLowerCase())
      if (selectedValue) {
        const option = variant.values?.find((v) => v.value === selectedValue)
        if (option?.price) {
          selectedPrices.push(option.price)
          // Assuming the last selected variant determines the price if multiple dimensions exist
          // or if there's only one dimension, this works.
          selectedVariantPrice = option.price
        }
      }
    })

    const allPrices = variants.flatMap(
      (v) =>
        v.values?.map((val) => val.price).filter((p): p is number => typeof p === 'number') || [],
    )
    if (allPrices.length > 0) {
      lowestAmount = Math.min(...(allPrices as number[]))
      highestAmount = Math.max(...(allPrices as number[]))
    }
  }

  if (product.basePrice && typeof product.basePrice === 'number') {
    amount = product.basePrice
  }

  // Override amount if a variant is selected
  if (selectedVariantPrice !== null) {
    amount = selectedVariantPrice
  }

  // If hovering an option, use that price
  const displayAmount = hoverPrice !== null ? hoverPrice : amount
  const showRange = hasVariants && selectedPrices.length === 0 && hoverPrice === null

  // Calculate manual discount for non-variant base price display
  const manualDiscount = manualDiscounts && manualDiscounts.length > 0 ? manualDiscounts[0] : null
  const manualDiscountPct = manualDiscount?.discount_percentage ?? 0
  const hasManualDiscount = !hasVariants && manualDiscountPct > 0 && displayAmount > 0
  const discountedDisplayAmount = hasManualDiscount
    ? calculateDiscountedPrice(displayAmount, manualDiscountPct)
    : displayAmount

  const priceBlock = (
    <div className="text-right">
      <div className="text-2xl font-bold text-white">
        <AnimatePresence mode="wait">
          <motion.div
            key={showRange ? 'range' : discountedDisplayAmount}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            {showRange ? (
              <Price highestAmount={highestAmount} lowestAmount={lowestAmount} size="large" />
            ) : (
              <Price
                amount={discountedDisplayAmount}
                originalAmount={hasManualDiscount ? displayAmount : undefined}
                size="large"
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      <span className="text-[10px] font-mono text-white/60 mt-1 block">
        {t('vatIncluded', { rate: product.customTaxRate ?? 20 })}
      </span>
      <div className="mt-4 border-b border-white/10" />
    </div>
  )

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex items-start justify-between gap-4">
          <h1
            className="text-3xl font-bold tracking-tight text-white leading-tight break-words flex-1"
            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
          >
            {product.title}
          </h1>
          <div className="flex items-center gap-2 mt-1 shrink-0">
            <WishlistButton
              productId={String(product.id)}
              variant="button"
              isLoggedIn={Boolean(user)}
              loginHref={`/${locale}/login`}
            />
            <ShareButton title={product.title} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Suspense fallback={<div className="h-6 w-24 animate-pulse bg-muted rounded-md" />}>
            <StockIndicator product={product} />
          </Suspense>

          {categories.length > 0 && (
            <>
              <span className="text-white/50">|</span>
              <div className="flex gap-2">
                {categories.map((category) => (
                  <span key={category.id} className="font-mono text-white/85">
                    {category.title}
                  </span>
                ))}
              </div>
            </>
          )}

          {hasManualDiscount && manualDiscountPct > 0 && (
            <>
              <span className="text-white/50">|</span>
              <motion.span
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold font-mono uppercase tracking-wider bg-red-600/90 text-white shadow-lg"
              >
                −{manualDiscountPct}%
              </motion.span>
            </>
          )}

          {bulkDiscounts && bulkDiscounts.length > 0 && (
            <>
              <span className="text-white/50">|</span>
              <motion.span
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold font-mono uppercase tracking-wider bg-purple-600/90 text-white shadow-lg"
              >
                {t('bulkDiscount')}
              </motion.span>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-6">
        {hasVariants ? (
          <div className="flex flex-col gap-2">
            <Suspense
              fallback={<div className="h-10 w-full animate-pulse bg-muted rounded-full" />}
            >
              <VariantSelector
                product={product}
                priceBlock={priceBlock}
                onHoverOption={setHoverPrice}
              />
            </Suspense>
          </div>
        ) : bulkDiscounts && bulkDiscounts.length > 0 ? (
          <BulkDiscountTable
            discounts={bulkDiscounts}
            basePrice={amount || lowestAmount || 0}
            priceBlock={priceBlock}
          />
        ) : (
          <div className="flex justify-end">{priceBlock}</div>
        )}

        {hasVariants && bulkDiscounts && bulkDiscounts.length > 0 && (
          <BulkDiscountTable discounts={bulkDiscounts} basePrice={amount || lowestAmount || 0} />
        )}

        <hr className="border-white/20" />

        <div className="flex items-stretch">
          <Suspense fallback={null}>
            <AddToCart product={product} />
          </Suspense>
        </div>
      </div>
    </div>
  )
}
